import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, Music, Loader2, Box, ArrowRightToLine } from 'lucide-react';

const TrackQueue = ({ 
  tracks = [],
  crates = [],
  onLoadToDeckA,
  onLoadToDeckB,
  isEnabled,
  deckAPlaying,
  deckBPlaying
}) => {
  // Crate selector state
  const [crateIndex, setCrateIndex] = useState(0);
  
  // Up Next states - each holds { crate, trackIndex, tracks[] }
  const [upNextA, setUpNextA] = useState({ crate: null, trackIndex: 0, tracks: [] });
  const [upNextB, setUpNextB] = useState({ crate: null, trackIndex: 0, tracks: [] });
  
  // Queued to load states (waiting for deck to stop)
  const [queuedA, setQueuedA] = useState(null);
  const [queuedB, setQueuedB] = useState(null);
  
  // Get current crate from list
  const currentCrate = crates.length > 0 ? crates[crateIndex] : null;
  
  // Navigate crates
  const nextCrate = () => {
    if (crates.length > 0) {
      setCrateIndex((prev) => (prev + 1) % crates.length);
    }
  };
  
  const prevCrate = () => {
    if (crates.length > 0) {
      setCrateIndex((prev) => (prev - 1 + crates.length) % crates.length);
    }
  };
  
  // Assign crate to deck's Up Next
  const assignCrateToDeck = (crate, deck) => {
    const crateTracks = crate.tracks || [];
    if (deck === 'A') {
      setUpNextA({ crate, trackIndex: 0, tracks: crateTracks });
    } else {
      setUpNextB({ crate, trackIndex: 0, tracks: crateTracks });
    }
  };
  
  // Navigate tracks in Up Next
  const nextTrackA = () => {
    if (upNextA.tracks.length > 0) {
      setUpNextA(prev => ({
        ...prev,
        trackIndex: (prev.trackIndex + 1) % prev.tracks.length
      }));
    }
  };
  
  const prevTrackA = () => {
    if (upNextA.tracks.length > 0) {
      setUpNextA(prev => ({
        ...prev,
        trackIndex: (prev.trackIndex - 1 + prev.tracks.length) % prev.tracks.length
      }));
    }
  };
  
  const nextTrackB = () => {
    if (upNextB.tracks.length > 0) {
      setUpNextB(prev => ({
        ...prev,
        trackIndex: (prev.trackIndex + 1) % prev.tracks.length
      }));
    }
  };
  
  const prevTrackB = () => {
    if (upNextB.tracks.length > 0) {
      setUpNextB(prev => ({
        ...prev,
        trackIndex: (prev.trackIndex - 1 + prev.tracks.length) % prev.tracks.length
      }));
    }
  };
  
  // Get current track for each Up Next
  const currentTrackA = upNextA.tracks.length > 0 ? upNextA.tracks[upNextA.trackIndex] : null;
  const currentTrackB = upNextB.tracks.length > 0 ? upNextB.tracks[upNextB.trackIndex] : null;
  
  // Quick Load - queue track to load when deck stops
  const quickLoadA = () => {
    if (currentTrackA) {
      if (!deckAPlaying) {
        onLoadToDeckA?.(currentTrackA);
      } else {
        setQueuedA(currentTrackA);
      }
    }
  };
  
  const quickLoadB = () => {
    if (currentTrackB) {
      if (!deckBPlaying) {
        onLoadToDeckB?.(currentTrackB);
      } else {
        setQueuedB(currentTrackB);
      }
    }
  };
  
  // Watch for deck stop to auto-load queued tracks
  useEffect(() => {
    if (!deckAPlaying && queuedA) {
      onLoadToDeckA?.(queuedA);
      setQueuedA(null);
    }
  }, [deckAPlaying, queuedA, onLoadToDeckA]);
  
  useEffect(() => {
    if (!deckBPlaying && queuedB) {
      onLoadToDeckB?.(queuedB);
      setQueuedB(null);
    }
  }, [deckBPlaying, queuedB, onLoadToDeckB]);
  
  // Cancel queued load
  const cancelQueuedA = () => setQueuedA(null);
  const cancelQueuedB = () => setQueuedB(null);
  
  return (
    <div className="track-queue-section" data-testid="track-queue-section">
      <div className="track-queue-row">
        {/* Up Next A - Compact */}
        <div className={`up-next-slot compact deck-a ${currentTrackA ? 'has-track' : ''} ${queuedA ? 'queued' : ''}`}>
          <div className="up-next-header">
            <span className="up-next-label">UP NEXT A</span>
            {upNextA.crate && (
              <span className="up-next-crate-name">{upNextA.crate.name}</span>
            )}
          </div>
          
          {currentTrackA ? (
            <div className="up-next-content-compact">
              <div className="up-next-track-row">
                <div className="up-next-art-small">
                  {currentTrackA.album_art || currentTrackA.artwork_url ? (
                    <img src={currentTrackA.album_art || currentTrackA.artwork_url} alt="" />
                  ) : (
                    <Music size={14} />
                  )}
                </div>
                <div className="up-next-info-compact">
                  <span className="up-next-title">{currentTrackA.title || currentTrackA.name}</span>
                  <span className="up-next-meta">
                    {currentTrackA.artist || currentTrackA.artists?.join(', ') || 'Unknown'}
                    {currentTrackA.bpm && <span className="bpm-tag">{Math.round(currentTrackA.bpm)}</span>}
                  </span>
                </div>
                <div className="up-next-controls">
                  <div className="track-nav-compact">
                    <button className="nav-btn-sm" onClick={prevTrackA}><ChevronUp size={12} /></button>
                    <span className="track-pos">{upNextA.trackIndex + 1}/{upNextA.tracks.length}</span>
                    <button className="nav-btn-sm" onClick={nextTrackA}><ChevronDown size={12} /></button>
                  </div>
                  {queuedA ? (
                    <button className="load-btn queued" onClick={cancelQueuedA}>
                      <Loader2 size={10} className="spinning" />
                    </button>
                  ) : (
                    <button className="load-btn" onClick={quickLoadA} disabled={!isEnabled} title={deckAPlaying ? 'Queue' : 'Quick Load'}>
                      <ArrowRightToLine size={10} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="up-next-empty-compact">Click A on a crate</div>
          )}
        </div>
        
        {/* Center - Crate Selector */}
        <div className="crate-selector-compact">
          <div className="crate-header-row">
            <div className="crate-icon"><Box size={14} /></div>
            <span className="crate-label">CRATES</span>
            {crates.length > 1 && (
              <div className="crate-nav-btns">
                <button className="crate-nav-btn-sm" onClick={prevCrate} title="Previous crate">
                  <ChevronUp size={12} />
                </button>
                <button className="crate-nav-btn-sm" onClick={nextCrate} title="Next crate">
                  <ChevronDown size={12} />
                </button>
              </div>
            )}
          </div>
          
          {crates.length > 0 && currentCrate ? (
            <div className="crate-card-compact">
              <div className="crate-info">
                <span className="crate-name">{currentCrate.name}</span>
                <span className="crate-track-count">{currentCrate.tracks?.length || 0} tracks</span>
              </div>
              <div className="crate-deck-btns">
                <button 
                  className="crate-btn deck-a"
                  onClick={() => assignCrateToDeck(currentCrate, 'A')}
                  title="Load to Up Next A"
                >
                  A
                </button>
                <button 
                  className="crate-btn deck-b"
                  onClick={() => assignCrateToDeck(currentCrate, 'B')}
                  title="Load to Up Next B"
                >
                  B
                </button>
              </div>
            </div>
          ) : (
            <div className="no-crates-compact">No crates</div>
          )}
        </div>
        
        {/* Up Next B - Compact */}
        <div className={`up-next-slot compact deck-b ${currentTrackB ? 'has-track' : ''} ${queuedB ? 'queued' : ''}`}>
          <div className="up-next-header">
            <span className="up-next-label">UP NEXT B</span>
            {upNextB.crate && (
              <span className="up-next-crate-name">{upNextB.crate.name}</span>
            )}
          </div>
          
          {currentTrackB ? (
            <div className="up-next-content-compact">
              <div className="up-next-track-row">
                <div className="up-next-art-small">
                  {currentTrackB.album_art || currentTrackB.artwork_url ? (
                    <img src={currentTrackB.album_art || currentTrackB.artwork_url} alt="" />
                  ) : (
                    <Music size={14} />
                  )}
                </div>
                <div className="up-next-info-compact">
                  <span className="up-next-title">{currentTrackB.title || currentTrackB.name}</span>
                  <span className="up-next-meta">
                    {currentTrackB.artist || currentTrackB.artists?.join(', ') || 'Unknown'}
                    {currentTrackB.bpm && <span className="bpm-tag">{Math.round(currentTrackB.bpm)}</span>}
                  </span>
                </div>
                <div className="up-next-controls">
                  <div className="track-nav-compact">
                    <button className="nav-btn-sm" onClick={prevTrackB}><ChevronUp size={12} /></button>
                    <span className="track-pos">{upNextB.trackIndex + 1}/{upNextB.tracks.length}</span>
                    <button className="nav-btn-sm" onClick={nextTrackB}><ChevronDown size={12} /></button>
                  </div>
                  {queuedB ? (
                    <button className="load-btn queued" onClick={cancelQueuedB}>
                      <Loader2 size={10} className="spinning" />
                    </button>
                  ) : (
                    <button className="load-btn" onClick={quickLoadB} disabled={!isEnabled} title={deckBPlaying ? 'Queue' : 'Quick Load'}>
                      <ArrowRightToLine size={10} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="up-next-empty-compact">Click B on a crate</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackQueue;
